import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaTimes, FaSun, FaMoon, FaPalette, FaChevronDown, FaEyeDropper } from 'react-icons/fa';
// Migrated to TypeScript data file
import { navItems } from '../data/navigation';
import { useTheme } from '../contexts/ThemeContext';
import { useColorScheme } from '../contexts/ColorSchemeContext';
import BatteryBar from './BatteryBar';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [customColor, setCustomColorValue] = useState('#D4AF37');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [showTitlePicker, setShowTitlePicker] = useState(false);
  const [showBodyPicker, setShowBodyPicker] = useState(false);
  const themeDropdownRef = useRef(null);
  
  const { 
    theme,
    mode,
    isDarkMode, 
    colorScheme,
    titleColorOverride,
    bodyColorOverride,
    setTheme,
    setMode,
    setColorScheme, 
    setCustomColor,
    setTitleColor,
    setBodyTextColor,
    clearTextColorOverrides
  } = useTheme();
  
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  const colorOptions = [
    { key: 'gold', name: 'Gold', color: '#D4AF37' },
    { key: 'blue', name: 'Blue', color: '#3B82F6' },
    { key: 'purple', name: 'Purple', color: '#8B5CF6' },
    { key: 'green', name: 'Green', color: '#10B981' },
    { key: 'red', name: 'Red', color: '#EF4444' }
  ];

  // Dynamic color options based on current theme
  const getTextColorOptions = (currentTheme, currentMode) => {
    if (currentMode === 'light') {
      return {
        title: [
          { name: 'Dark Gray', color: '#1F2937' },
          { name: 'Near Black', color: '#111827' },
          { name: 'Medium Dark', color: '#374151' },
          { name: 'Charcoal', color: '#1F2937' },
          { name: 'Slate', color: '#475569' },
          { name: 'Dark Blue', color: '#1E3A8A' }
        ],
        body: [
          { name: 'Dark Gray', color: '#4B5563' },
          { name: 'Medium Gray', color: '#6B7280' },
          { name: 'Darker Gray', color: '#374151' },
          { name: 'Slate Gray', color: '#64748B' },
          { name: 'Blue Gray', color: '#475569' },
          { name: 'Warm Gray', color: '#6B7280' }
        ]
      };
    } else if (currentTheme === 'sepia') {
      return {
        title: [
          { name: 'Dark Brown', color: '#4A3728' },
          { name: 'Brown', color: '#6B4423' },
          { name: 'Tan', color: '#8B5A2B' },
          { name: 'Saddle Brown', color: '#8B4513' },
          { name: 'Dark Tan', color: '#5D4037' },
          { name: 'Coffee', color: '#3E2723' }
        ],
        body: [
          { name: 'Medium Brown', color: '#7A5C42' },
          { name: 'Light Brown', color: '#9B7653' },
          { name: 'Darker Brown', color: '#6B5446' },
          { name: 'Sepia', color: '#8B7355' },
          { name: 'Warm Brown', color: '#A0522D' },
          { name: 'Muted Brown', color: '#8B6F47' }
        ]
      };
    } else { // dark theme
      return {
        title: [
          { name: 'Pure White', color: '#FFFFFF' },
          { name: 'Off White', color: '#F9FAFB' },
          { name: 'Light Gray', color: '#F3F4F6' },
          { name: 'Cool White', color: '#F0F9FF' },
          { name: 'Soft White', color: '#FEFEFE' },
          { name: 'Warm White', color: '#FEF3C7' }
        ],
        body: [
          { name: 'Light Gray', color: '#D1D5DB' },
          { name: 'Medium Gray', color: '#9CA3AF' },
          { name: 'Lighter Gray', color: '#E5E7EB' },
          { name: 'Blue Gray', color: '#94A3B8' },
          { name: 'Cool Gray', color: '#9CA3AF' },
          { name: 'Muted Purple', color: '#A78BFA' }
        ]
      };
    }
  };

  const textColorOptions = getTextColorOptions(theme, mode);

  // Custom Color Picker Component
  const CustomColorPicker = ({ isOpen, onClose, currentColor, onChange, label }) => {
    const [tempColor, setTempColor] = useState(currentColor);
    const pickerRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (pickerRef.current && !pickerRef.current.contains(event.target)) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, onClose]);

    const handleSave = () => {
      onChange(tempColor);
      onClose();
    };

    if (!isOpen) return null;

    return (
      <div 
        ref={pickerRef}
        className={`absolute top-full left-0 mt-2 p-4 rounded-lg shadow-xl border z-50 ${colors.cardBackground} ${colors.borderColor}`}
        style={{ minWidth: '200px' }}
      >
        <h4 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
          {label}
        </h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div 
              className="w-12 h-12 rounded border-2 border-gray-300 dark:border-gray-600"
              style={{ backgroundColor: tempColor }}
            />
            <div className="flex-1">
              <input
                type="color"
                value={tempColor}
                onChange={(e) => setTempColor(e.target.value)}
                className="w-full h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-all duration-300 ${colors.bgAccent} text-white hover:opacity-90`}
            >
              Save
            </button>
            <button
              onClick={onClose}
              className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-all duration-300 ${colors.textSecondary} hover:${colors.cardBackground} border border-gray-300 dark:border-gray-700`}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };


  const handleColorSchemeChange = (scheme) => {
    setColorScheme(scheme);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = navItems.map(item => item.id);
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside theme dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (themeDropdownRef.current && !themeDropdownRef.current.contains(event.target)) {
        setIsThemeOpen(false);
      }
    };

    if (isThemeOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isThemeOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
      isScrolled 
        ? `${colors.cardBackground} backdrop-blur-md shadow-lg` 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => scrollToSection('hero')}
              className="hover:scale-105 transition-transform duration-300"
            >
              <BatteryBar />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 flex-1 justify-evenly max-w-4xl">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                  activeSection === item.id
                    ? `${colors.bg} ${colors.textPrimary} font-semibold`
                    : `${colors.textSecondary} hover:${colors.cardBackground} hover:${colors.text}`
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* Theme Toggle */}
          <div className="hidden lg:flex items-center">
            <div className="relative" ref={themeDropdownRef}>
              <button
                onClick={() => setIsThemeOpen(!isThemeOpen)}
                className={`p-2 rounded-full transition-all duration-300 ${colors.cardBackground} hover:opacity-80 ${colors.textPrimary}`}
                aria-label="Theme settings"
              >
                <div className="flex items-center space-x-1">
                  {isDarkMode ? <FaMoon className="w-4 h-4" /> : <FaSun className="w-4 h-4" />}
                  <FaPalette className="w-4 h-4" />
                  <FaChevronDown className={`w-3 h-3 transition-transform duration-200 ${isThemeOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* Theme Dropdown */}
              {isThemeOpen && (
                <div className={`absolute top-full right-0 mt-2 w-72 rounded-lg shadow-xl border transition-all duration-300 ${colors.cardBackground} ${colors.borderColor}`}>
                  <div className="p-4 space-y-4">
                    {/* Theme Type Selector */}
                    <div>
                      <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
                        Theme Type
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setTheme('default')}
                          className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                            theme === 'default'
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: '#FFFFFF' }}
                          title="Default Theme"
                        >
                          <span className="text-xs text-gray-800 font-bold">D</span>
                        </button>
                        <button
                          onClick={() => setTheme('sepia')}
                          className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                            theme === 'sepia'
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: '#D2B48C' }}
                          title="Sepia Theme"
                        >
                          <span className="text-xs text-amber-800 font-bold">S</span>
                        </button>
                        <button
                          onClick={() => setTheme('dracula')}
                          className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                            theme === 'dracula'
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: '#282A36' }}
                          title="Dracula Theme"
                        >
                          <span className="text-xs text-purple-300 font-bold">D</span>
                        </button>
                      </div>
                    </div>

                    {/* Light/Dark Mode Toggle */}
                    <div>
                      <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
                        Mode
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setMode('light')}
                          className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                            mode === 'light'
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: '#FFFFFF' }}
                          title="Light Mode"
                        >
                          <FaSun className="w-3 h-3 text-yellow-500" />
                        </button>
                        <button
                          onClick={() => setMode('dark')}
                          className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                            mode === 'dark'
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: '#1F2937' }}
                          title="Dark Mode"
                        >
                          <FaMoon className="w-3 h-3 text-blue-300" />
                        </button>
                      </div>
                    </div>

                    {/* Accent Color Selector */}
                    <div className="relative">
                      <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
                        Accent Color
                      </h3>
                      <div className="flex items-center space-x-2">
                        {colorOptions.slice(0, 4).map((option) => (
                          <button
                            key={option.key}
                            onClick={() => handleColorSchemeChange(option.key)}
                            className={`w-8 h-8 rounded border-2 transition-all duration-300 ${
                              colorScheme === option.key
                                ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                                : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                            }`}
                            style={{ backgroundColor: option.color }}
                            title={option.name}
                          />
                        ))}
                        <button
                          onClick={() => setShowCustomPicker(!showCustomPicker)}
                          className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center relative ${
                            colorScheme === 'custom'
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: customColor }}
                          title="Custom Color"
                        >
                          <FaEyeDropper className="w-3 h-3 text-white" />
                        </button>
                        <CustomColorPicker
                          isOpen={showCustomPicker}
                          onClose={() => setShowCustomPicker(false)}
                          currentColor={customColor}
                          onChange={(color) => {
                            setCustomColorValue(color);
                            setCustomColor(color);
                            handleColorSchemeChange('custom');
                          }}
                          label="Custom Accent Color"
                        />
                      </div>
                    </div>

                    {/* Text Color Customization */}
                    <div>
                      <h3 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
                        Text Colors {(titleColorOverride || bodyColorOverride) && <span className="text-xs font-normal ml-1">(Custom)</span>}
                      </h3>
                      
                      {/* Title Color */}
                      <div className="mb-3">
                        <label className={`text-xs ${colors.textSecondary} mb-1 block`}>Title Color</label>
                        <div className="flex items-center space-x-2">
                          {textColorOptions.title.map((option, idx) => (
                            <button
                              key={idx}
                              onClick={() => setTitleColor(option.color)}
                              className={`w-6 h-6 rounded border-2 transition-all duration-300 ${
                                titleColorOverride === option.color
                                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                              }`}
                              style={{ backgroundColor: option.color }}
                              title={option.name}
                            />
                          ))}
                          <button
                            onClick={() => setShowTitlePicker(!showTitlePicker)}
                            className={`w-6 h-6 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                              showTitlePicker
                                ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                                : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                            }`}
                            style={{ backgroundColor: titleColorOverride || '#666' }}
                            title="Custom Title Color"
                          >
                            <FaEyeDropper className="w-2 h-2 text-white" />
                          </button>
                          <CustomColorPicker
                            isOpen={showTitlePicker}
                            onClose={() => setShowTitlePicker(false)}
                            currentColor={titleColorOverride || '#000000'}
                            onChange={setTitleColor}
                            label="Title Color"
                          />
                        </div>
                      </div>

                      {/* Body Color */}
                      <div className="mb-2">
                        <label className={`text-xs ${colors.textSecondary} mb-1 block`}>Body Color</label>
                        <div className="flex items-center space-x-2">
                          {textColorOptions.body.map((option, idx) => (
                            <button
                              key={idx}
                              onClick={() => setBodyTextColor(option.color)}
                              className={`w-6 h-6 rounded border-2 transition-all duration-300 ${
                                bodyColorOverride === option.color
                                  ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                                  : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                              }`}
                              style={{ backgroundColor: option.color }}
                              title={option.name}
                            />
                          ))}
                          <button
                            onClick={() => setShowBodyPicker(!showBodyPicker)}
                            className={`w-6 h-6 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                              showBodyPicker
                                ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                                : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                            }`}
                            style={{ backgroundColor: bodyColorOverride || '#999' }}
                            title="Custom Body Color"
                          >
                            <FaEyeDropper className="w-2 h-2 text-white" />
                          </button>
                          <CustomColorPicker
                            isOpen={showBodyPicker}
                            onClose={() => setShowBodyPicker(false)}
                            currentColor={bodyColorOverride || '#666666'}
                            onChange={setBodyTextColor}
                            label="Body Text Color"
                          />
                        </div>
                      </div>

                      {/* Clear Overrides */}
                      {(titleColorOverride || bodyColorOverride) && (
                        <button
                          onClick={clearTextColorOverrides}
                          className={`text-xs ${colors.textSecondary} hover:${colors.text} underline mt-1`}
                        >
                          Reset to theme colors
                        </button>
                      )}
                    </div>

                    {/* Reset Button */}
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => {
                          setColorScheme('gold');
                          setTheme('default');
                          setMode('dark');
                          clearTextColorOverrides();
                        }}
                        className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${colors.textSecondary} hover:${colors.cardBackground} border border-gray-300 dark:border-gray-700`}
                      >
                        Reset to Default
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
          >
            {isOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className={`lg:hidden absolute top-full left-0 right-0 ${colors.cardBackground} shadow-lg border-t border-gray-300 dark:border-gray-700`}>
            <div className="px-4 py-2 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                    activeSection === item.id
                      ? `${colors.bg} text-gray-900 font-semibold`
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              ))}
              
              {/* Mobile Theme Settings */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 space-y-4">
                <h3 className={`text-sm font-semibold ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Theme Settings
                </h3>
                
                {/* Theme Type Selector */}
                <div>
                  <h4 className={`text-xs font-medium mb-2 ${colors.textSecondary}`}>
                    Theme Type
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setTheme('default')}
                      className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                        theme === 'default'
                          ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#FFFFFF' }}
                      title="Default Theme"
                    >
                      <span className="text-xs text-gray-800 font-bold">D</span>
                    </button>
                    <button
                      onClick={() => setTheme('sepia')}
                      className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                        theme === 'sepia'
                          ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#D2B48C' }}
                      title="Sepia Theme"
                    >
                      <span className="text-xs text-amber-800 font-bold">S</span>
                    </button>
                    <button
                      onClick={() => setTheme('dracula')}
                      className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                        theme === 'dracula'
                          ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#282A36' }}
                      title="Dracula Theme"
                    >
                      <span className="text-xs text-purple-300 font-bold">D</span>
                    </button>
                  </div>
                </div>

                {/* Light/Dark Mode Toggle */}
                <div>
                  <h4 className={`text-xs font-medium mb-2 ${colors.textSecondary}`}>
                    Mode
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setMode('light')}
                      className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                        mode === 'light'
                          ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#FFFFFF' }}
                      title="Light Mode"
                    >
                      <FaSun className="w-3 h-3 text-yellow-500" />
                    </button>
                    <button
                      onClick={() => setMode('dark')}
                      className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                        mode === 'dark'
                          ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#1F2937' }}
                      title="Dark Mode"
                    >
                      <FaMoon className="w-3 h-3 text-blue-300" />
                    </button>
                  </div>
                </div>

                {/* Accent Color Selector */}
                <div>
                  <h4 className={`text-xs font-medium mb-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Accent Color
                  </h4>
                  <div className="flex items-center space-x-2">
                    {colorOptions.slice(0, 4).map((option) => (
                      <button
                        key={option.key}
                        onClick={() => handleColorSchemeChange(option.key)}
                        className={`w-8 h-8 rounded border-2 transition-all duration-300 ${
                          colorScheme === option.key
                            ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                            : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                        }`}
                        style={{ backgroundColor: option.color }}
                        title={option.name}
                      />
                    ))}
                    <button
                      onClick={() => setShowCustomPicker(!showCustomPicker)}
                      className={`w-8 h-8 rounded border-2 transition-all duration-300 flex items-center justify-center ${
                        colorScheme === 'custom'
                          ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                      }`}
                      style={{ backgroundColor: customColor }}
                      title="Custom Color"
                    >
                      <FaEyeDropper className="w-3 h-3 text-white" />
                    </button>
                  </div>
                </div>

                {/* Text Color Customization */}
                <div>
                  <h4 className={`text-xs font-medium mb-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Text Colors {(titleColorOverride || bodyColorOverride) && <span className="text-xs font-normal">(Custom)</span>}
                  </h4>
                  
                  {/* Title Color */}
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 mb-1 block">Title</label>
                    <div className="flex items-center space-x-2">
                      {textColorOptions.title.slice(0, 5).map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => setTitleColor(option.color)}
                          className={`w-6 h-6 rounded border-2 transition-all duration-300 ${
                            titleColorOverride === option.color
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: option.color }}
                          title={option.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Body Color */}
                  <div className="mb-2">
                    <label className="text-xs text-gray-500 mb-1 block">Body</label>
                    <div className="flex items-center space-x-2">
                      {textColorOptions.body.slice(0, 5).map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => setBodyTextColor(option.color)}
                          className={`w-6 h-6 rounded border-2 transition-all duration-300 ${
                            bodyColorOverride === option.color
                              ? 'border-gray-400 dark:border-gray-500 shadow-md scale-110'
                              : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: option.color }}
                          title={option.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Clear Overrides */}
                  {(titleColorOverride || bodyColorOverride) && (
                    <button
                      onClick={clearTextColorOverrides}
                      className="text-xs text-gray-500 hover:text-gray-700 underline mt-1"
                    >
                      Reset to theme colors
                    </button>
                  )}
                </div>

                {/* Reset Button */}
                <button
                  onClick={() => {
                    setColorScheme('gold');
                    setTheme('default');
                    setMode('dark');
                    clearTextColorOverrides();
                  }}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${colors.textSecondary} hover:${colors.cardBackground} border border-gray-300 dark:border-gray-700`}
                >
                  Reset to Default
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
